// iDevAffiliate matches a paying customer to an affiliate click by the visitor's
// IP address (ClickUp z94kydf5tq). Its own script reports that address from the
// same host that recorded the click, so it is read from there rather than
// guessed from our side: the two can differ in address family. The script
// assigns the address to an element with a fixed id.
//
// That script is third-party code and the signup page holds the password
// field, so it never runs in the page. It runs inside a sandboxed iframe with
// no `allow-same-origin`: the frame gets an opaque origin and cannot read the
// page's DOM, cookies or storage, cannot navigate the page, open windows or
// submit forms. All it can do is post the address string back to us.
const IDEV_IP_SCRIPT_URL =
  "https://socialchamp.idevaffiliate.com/connect/stripe_ip.php";
const IDEV_IP_ELEMENT_ID = "idev_custom_x21";
const IDEV_IP_TIMEOUT_MS = 4000;
const IDEV_IP_SANDBOX = "allow-scripts";
const IP_MAX_LENGTH = 45;

let pendingLookup = null;
let cachedAffiliateIp = "";

const cleanIp = (value) =>
  typeof value === "string" ? value.trim().slice(0, IP_MAX_LENGTH) : "";

// Runs inside the frame only. It reports through postMessage because the frame
// cannot touch the page, and always reports once, even when the script fails.
const buildSandboxedDocument = () =>
  [
    "<!doctype html><meta charset=utf-8>",
    `<input type=hidden id="${IDEV_IP_ELEMENT_ID}">`,
    `<script src="${IDEV_IP_SCRIPT_URL}"`,
    ` onload="parent.postMessage({ idevIp: document.getElementById('${IDEV_IP_ELEMENT_ID}').value }, '*')"`,
    ` onerror="parent.postMessage({ idevIp: '' }, '*')"></script>`,
  ].join("");

/**
 * Starts (once per page) the lookup and resolves with the address, or "" when
 * the script is blocked, slow or fails. It never rejects and never throws:
 * signup must work with the lookup unavailable, and the server falls back to
 * the request address.
 */
const prefetchAffiliateIp = (doc = globalThis.document) => {
  if (pendingLookup) {
    return pendingLookup;
  }

  pendingLookup = new Promise((resolve) => {
    let frame;
    let win;
    let timer;
    let settled = false;

    const onMessage = (event) => {
      // Only the frame this lookup created, and only the one expected shape.
      if (!frame || !event || event.source !== frame.contentWindow) {
        return;
      }
      const data = event.data;
      if (data && typeof data === "object" && typeof data.idevIp === "string") {
        finish(data.idevIp);
      }
    };

    const finish = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      cachedAffiliateIp = cleanIp(value);
      try {
        win && win.removeEventListener("message", onMessage);
        frame && frame.parentNode && frame.parentNode.removeChild(frame);
      } catch {
        // Cleanup is cosmetic.
      }
      resolve(cachedAffiliateIp);
    };

    try {
      win = doc && doc.defaultView;
      if (
        !win ||
        !doc.body ||
        typeof doc.createElement !== "function" ||
        typeof win.addEventListener !== "function"
      ) {
        finish("");
        return;
      }
      win.addEventListener("message", onMessage);
      timer = setTimeout(() => finish(""), IDEV_IP_TIMEOUT_MS);

      frame = doc.createElement("iframe");
      frame.setAttribute("sandbox", IDEV_IP_SANDBOX);
      frame.setAttribute("aria-hidden", "true");
      frame.setAttribute("tabindex", "-1");
      frame.setAttribute("title", "");
      frame.style.display = "none";
      frame.srcdoc = buildSandboxedDocument();
      doc.body.appendChild(frame);
    } catch {
      finish("");
    }
  });

  return pendingLookup;
};

/** The address found so far, or "" while the lookup is pending or failed. */
const getCachedAffiliateIp = () => cachedAffiliateIp;

const resetAffiliateIpForTests = () => {
  pendingLookup = null;
  cachedAffiliateIp = "";
};

module.exports = {
  IDEV_IP_ELEMENT_ID,
  IDEV_IP_SANDBOX,
  IDEV_IP_SCRIPT_URL,
  buildSandboxedDocument,
  getCachedAffiliateIp,
  prefetchAffiliateIp,
  resetAffiliateIpForTests,
};
